import { storyblokEditable } from "@storyblok/react";
import { useState, useEffect } from "react";
import { customRenderRichText } from "../utils/renderRichText";

const resolveStoryblokLink = (link) => {
  if (!link) return "#";

  if (link.linktype === "story") {
    const slug = link.cached_url || link.story?.full_slug;
    return slug ? `/${slug}` : "#";
  }

  if (link.linktype === "asset") {
    return link.url || "#";
  }

  return link.url || link.cached_url || "#";
};

const FiftyFifty = ({ blok }) => {
  const isDark = blok.dark_background;
  const [isSmallImage, setIsSmallImage] = useState(false);

  useEffect(() => {
    if (!blok.image?.filename) {
      setIsSmallImage(false);
      return;
    }

    const img = new Image();
    img.src = blok.image.filename;
    img.onload = () => {
      setIsSmallImage(img.width < 720 || img.height < 763);
    };
  }, [blok.image?.filename]);

  const primaryHref = resolveStoryblokLink(blok.button_link);
  const secondaryHref = resolveStoryblokLink(blok.secondary_button_link);

  return (
    <section
      {...storyblokEditable(blok)}
      className={`w-full flex flex-col md:flex-row items-stretch md:h-[763px] ${
        blok.reverse_layout ? "md:flex-row-reverse" : ""
      }`}
    >
      {/* Image */}
      <div
        className="
          w-full md:w-1/2
          h-auto md:h-full
          flex justify-center items-center
          overflow-hidden
        "
        style={{ backgroundColor: "#00033E" }}
      >
        {blok.image?.filename && (
          <img
            src={blok.image.filename}
            alt={blok.title}
            className={
              isSmallImage
                ? // small images: always contain
                  "w-full max-w-[720px] object-contain"
                : // large images: contain on mobile, cover on desktop
                  "w-full object-contain md:h-full md:object-cover"
            }
          />
        )}
      </div>

      {/* Text */}
      <div
        className={`w-full md:w-1/2 h-auto md:h-full flex flex-col justify-center px-6 md:px-12 py-10 md:py-16 ${
          isDark ? "bg-[#00033E] text-white" : "bg-white text-[#333]"
        }`}
      >
        <div className="max-w-[540px]">
          {blok.title && (
            <h2
              className={`
                font-bold mb-4 font-['Roboto']
                ${isDark ? "text-white" : "text-black"}
                text-[clamp(22px,2.3vw,36px)]
              `}
            >
              {blok.title}
            </h2>
          )}

          {/* Richtext Description Paragraph */}
          <div
            className="
              font-['Roboto']
              leading-relaxed
              mb-6
              text-[clamp(14px,1.1vw,18px)]
            "
          >
            {customRenderRichText(blok.description)}
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap items-center gap-6">
            {blok.button_text && (
              <a
                href={primaryHref}
                className="flex items-center justify-center gap-2 bg-[#1371DC] text-white rounded-full w-[150px] h-[34px] font-['Roboto'] text-sm font-medium"
              >
                {blok.primary_button_icon?.filename && (
                  <img
                    src={blok.primary_button_icon.filename}
                    alt={blok.primary_button_icon.alt || ""}
                    className="w-[19px] h-[14px] object-contain"
                  />
                )}
                {blok.button_text}
              </a>
            )}

            {blok.secondary_button && (
              <a
                href={secondaryHref}
                className={`flex items-center gap-2 font-['Roboto'] text-sm font-medium hover:underline ${
                  isDark ? "text-white" : "text-black"
                }`}
              >
                {blok.secondary_button}

                {blok.secondary_icon?.filename && (
                  <img
                    src={blok.secondary_icon.filename}
                    alt={blok.secondary_icon.alt || ""}
                    width={10}
                    height={10}
                    className="inline-block object-contain"
                  />
                )}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FiftyFifty;
